import {
  AccountState,
  Occupier,
} from "@/models/dcaAllocation/dcaAllocation.types"

/**
 * The four segment rows. PSR is deliberately not a dimension: the allocation
 * table is keyed on (occupier, accountState, placementNumber) only, so a PSR row
 * could never hold a different split from its neighbour.
 *
 * Change type is not a dimension either, which is what keeps this at four rows
 * rather than twenty-four.
 */
export interface ISegmentRow {
  id: string
  occupier: Occupier
  accountState: AccountState
  label: string
  /**
   * The placement the Moriarty litigation gate follows, or null where the segment
   * has no gate. Moriarty is a step in the journey but not a numbered placement, and
   * its position is owned by the Data-side pipeline, so it is seeded rather than
   * configured here.
   */
  gateAfterStep: number | null
}

export const SEGMENT_ROWS: ISegmentRow[] = [
  {
    id: "known-live",
    occupier: Occupier.Known,
    accountState: AccountState.Live,
    label: "Known · Live",
    gateAfterStep: 2,
  },
  {
    id: "known-final",
    occupier: Occupier.Known,
    accountState: AccountState.Final,
    label: "Known · Final",
    gateAfterStep: 1,
  },
  {
    id: "occupier-live",
    occupier: Occupier.Occupier,
    accountState: AccountState.Live,
    label: "Occupier · Live",
    gateAfterStep: null,
  },
  {
    id: "occupier-final",
    occupier: Occupier.Occupier,
    accountState: AccountState.Final,
    label: "Occupier · Final",
    gateAfterStep: null,
  },
]

/**
 * Placements shown. Under the simplified config Moriarty does not consume a
 * placement number, so there is no 4th placement - three is the design rather than
 * a limit worked around.
 */
export const PLACEMENT_NUMBERS: number[] = [1, 2, 3]

/**
 * One label per placement, the same for every segment. These used to carry two
 * numbers each - "2nd / 3rd placement" - because an accepted Moriarty referral
 * consumed a number and the two routes through the gate landed differently. The
 * simplified config removes that: a 2nd placement is a 2nd placement whichever way
 * the account came.
 */
export const PLACEMENT_LABELS: Record<number, string> = {
  1: "1st placement",
  2: "2nd placement",
  3: "3rd placement",
}

export const segmentRowFor = (
  occupier: Occupier,
  accountState: AccountState,
): ISegmentRow | undefined =>
  SEGMENT_ROWS.find(
    (row) => row.occupier === occupier && row.accountState === accountState,
  )

export const placementLabel = (placementNumber: number): string =>
  PLACEMENT_LABELS[placementNumber] ?? `${placementNumber} placement`

/** A split may total exactly 100, or be empty to retire the step. */
export const TOTAL_REQUIRED = 100

export const isValidTotal = (total: number, count: number): boolean =>
  count === 0 || total === TOTAL_REQUIRED
